import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Star, MapPin, MessageCircle, Briefcase, Calendar as CalIcon, Clock, Heart } from "lucide-react";
import { toast } from "sonner";

const TV_MOUNT_FEE = 80;
const TIP_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 50];

export default function HandymanDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [h, setH] = useState(null);
  const [cats, setCats] = useState([]);
  const [addons, setAddons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    category: "", description: "", scheduled_date: new Date(),
    scheduled_time: "10:00", estimated_hours: 2, address: "",
    addon_ids: [], tip_amount: 0,
  });

  useEffect(() => {
    api.get(`/handymen/${id}`).then((r) => setH(r.data)).catch(() => nav("/browse"));
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/addons").then((r) => setAddons(r.data));
  }, [id, nav]);

  // Calendar's `disabled` prop must be memoized with a stable identity or the calendar re-mounts on every render.
  const disabledDates = useMemo(() => ({ before: new Date() }), []);

  if (!h) return <div className="p-12 text-center">Loading…</div>;
  const catMap = Object.fromEntries(cats.map((c) => [c.id, c.label]));
  const addonMap = Object.fromEntries(addons.map((a) => [a.id, a]));
  const addonTotal = form.addon_ids.reduce((s, aid) => s + (addonMap[aid]?.price || 0), 0);
  const laborTotal = (Number(h.hourly_rate) || 0) * (Number(form.estimated_hours) || 0);
  const mountFee = form.category === "tv_mounting" ? TV_MOUNT_FEE : 0;
  const bundleSubtotal = laborTotal + addonTotal; // mount fee is NOT eligible for the bundle discount
  const subtotal = bundleSubtotal + mountFee;
  const selectedCategories = new Set(form.addon_ids.map((aid) => addonMap[aid]?.category));
  const bundleEligible = form.category === "tv_mounting" && selectedCategories.has("tv") && selectedCategories.has("tv_mount");
  const bundleDiscount = bundleEligible ? bundleSubtotal * 0.10 : 0;
  const baseAmount = bundleSubtotal - bundleDiscount;
  const grandTotal = baseAmount + mountFee;
  // Split payment (matches backend: 25% labor deposit + 100% merchandise + full mount fee upfront).
  const discRatio = bundleSubtotal > 0 ? 1 - bundleDiscount / bundleSubtotal : 1;
  const discountedLabor = laborTotal * discRatio;
  const discountedAddons = addonTotal * discRatio;
  const laborDeposit = discountedLabor * 0.25;
  const laborBalance = discountedLabor - laborDeposit;
  const tip = Number(form.tip_amount) || 0;
  const dueToday = laborDeposit + discountedAddons + mountFee + tip;

  const toggleAddon = (aid) => {
    setForm((f) => ({
      ...f,
      addon_ids: f.addon_ids.includes(aid) ? f.addon_ids.filter((x) => x !== aid) : [...f.addon_ids, aid],
    }));
  };

  const startMessage = async () => {
    if (!user) { nav("/login"); return; }
    nav(`/messages/${h.user_id}`);
  };

  const submitBooking = async () => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "student") { toast.error("Only students can book."); return; }
    if (!form.category || !form.description || !form.address) { toast.error("Please fill all fields."); return; }
    const [hh, mm] = form.scheduled_time.split(":");
    const when = new Date(form.scheduled_date);
    when.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
    try {
      const { data } = await api.post("/bookings", {
        handyman_id: h.user_id,
        category: form.category,
        description: form.description,
        scheduled_at: when.toISOString(),
        estimated_hours: Number(form.estimated_hours),
        address: form.address,
        addon_ids: form.addon_ids,
        tip_amount: Number(form.tip_amount) || 0,
      });
      toast.success("Booking created — redirecting to payment…");
      // Create checkout session
      const { data: ck } = await api.post("/checkout/session", {
        booking_id: data.booking_id,
        origin_url: window.location.origin,
      });
      window.location.href = ck.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="bg-[#131C2E] rounded-2xl border border-[#1F2A40] overflow-hidden">
            <div className="aspect-[16/9] overflow-hidden bg-beige">
              {h.photo_url && <img src={h.photo_url} alt={h.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-8">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <h1 className="font-display text-4xl text-charcoal">{h.name}</h1>
                  <p className="text-charcoal/60 mt-2 flex items-center gap-3">
                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {h.campus}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> {h.years_experience} yrs</span>
                  </p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <Star className="w-5 h-5 fill-terracotta text-terracotta" />
                    <span className="font-semibold text-charcoal">{Number(h.rating || 0).toFixed(1)}</span>
                    <span className="text-charcoal/60 text-sm">({h.review_count || 0})</span>
                  </div>
                  <p className="text-2xl font-display text-terracotta mt-1">${h.hourly_rate}<span className="text-base text-charcoal/60">/hr</span></p>
                </div>
              </div>

              <p className="mt-6 text-charcoal/80 leading-relaxed text-lg">{h.bio}</p>

              <div className="mt-6 flex flex-wrap gap-2">
                {(h.services || []).map((s) => (
                  <Badge key={s} className="rounded-full bg-beige text-charcoal/80 hover:bg-beige/70" data-testid={`service-${s}`}>
                    {catMap[s] || s}
                  </Badge>
                ))}
              </div>

              <div className="flex gap-3 mt-8">
                <Button
                  onClick={() => h.handyman_active !== false && setOpen(true)}
                  disabled={h.handyman_active === false}
                  data-testid="open-booking-btn"
                  className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white btn-lift h-12 px-6 disabled:opacity-50 disabled:cursor-not-allowed">
                  <CalIcon className="w-4 h-4 mr-2" />
                  {h.handyman_active === false ? "Not accepting bookings" : `Book ${h.name.split(" ")[0]}`}
                </Button>
                <Button onClick={startMessage} variant="outline" data-testid="message-btn"
                  className="rounded-full border-charcoal/20 hover:bg-beige h-12 px-6">
                  <MessageCircle className="w-4 h-4 mr-2" /> Message
                </Button>
              </div>

              {h.handyman_active === false && (
                <p className="mt-4 text-sm text-charcoal/60 bg-[#0B1220] border border-[#1F2A40] rounded-xl p-4" data-testid="inactive-notice">
                  This pro is still finalizing their onboarding. They'll be bookable once their account is activated — feel free to message them.
                </p>
              )}
            </div>
          </div>

          {/* Reviews */}
          <div className="mt-8">
            <h2 className="font-display text-2xl text-charcoal mb-4">Recent reviews</h2>
            {(h.reviews || []).length === 0 ? (
              <p className="text-charcoal/60">No reviews yet — be the first!</p>
            ) : (
              <div className="space-y-4">
                {h.reviews.map((r) => (
                  <div key={r.review_id} className="bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{r.student_name}</span>
                      <div className="flex">{[...Array(r.rating)].map((_, i) => <Star key={`${r.review_id}-star-${i}`} className="w-4 h-4 fill-terracotta text-terracotta" />)}</div>
                    </div>
                    <p className="text-charcoal/75 mt-2">{r.comment}</p>
                    {r.reply && (
                      <div className="mt-3 rounded-lg bg-terracotta/5 border-l-2 border-terracotta pl-3 py-2" data-testid={`review-reply-${r.review_id}`}>
                        <p className="text-xs text-terracotta/80 font-medium">Response from {h.name}</p>
                        <p className="text-charcoal/80 text-sm mt-1">{r.reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Booking sidebar */}
        <div className="lg:sticky lg:top-24 self-start">
          {open && (
            <div className="bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-6 animate-fade-up">
              <h3 className="font-display text-xl text-charcoal mb-4">Book a time</h3>
              <div className="space-y-4">
                <div>
                  <Label>Service</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="booking-category" className="mt-1 rounded-xl h-11"><SelectValue placeholder="Pick a service" /></SelectTrigger>
                    <SelectContent>
                      {(h.services || []).map((s) => <SelectItem key={s} value={s}>{catMap[s] || s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <div className="rounded-xl border border-[#1F2A40] mt-1 p-2">
                    <Calendar mode="single" selected={form.scheduled_date}
                      onSelect={(d) => d && setForm({ ...form, scheduled_date: d })}
                      disabled={disabledDates} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Time</Label>
                    <Input type="time" value={form.scheduled_time}
                      onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
                      data-testid="booking-time" className="mt-1 rounded-xl h-11" />
                  </div>
                  <div>
                    <Label>Hours</Label>
                    <Input type="number" min={1} step={0.5} value={form.estimated_hours}
                      onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })}
                      data-testid="booking-hours" className="mt-1 rounded-xl h-11" />
                  </div>
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Move-in address" data-testid="booking-address" className="mt-1 rounded-xl h-11" />
                </div>
                <div>
                  <Label>Describe the job</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3} placeholder="IKEA bed frame + two shelves…" data-testid="booking-description" className="mt-1 rounded-xl" />
                </div>

                {addons.length > 0 && (
                  <div>
                    <Label>Products (optional)</Label>
                    <p className="text-xs text-charcoal/50 mt-0.5 mb-2">Add hardware or a TV we stock — they'll bring it to the job.</p>
                    <div className="space-y-4">
                      {[
                        { key: "tv_mount", title: "TV Mounts" },
                        { key: "tv",       title: "Televisions" },
                        { key: "hardware", title: "Hardware (screws, hangers, etc.)" },
                        { key: "wallpaper", title: "Wallpaper Supplies" },
                        { key: "painting",  title: "Painting Supplies" },
                      ].map((group) => {
                        const items = addons.filter((a) => a.category === group.key);
                        if (items.length === 0) return null;
                        return (
                          <div key={group.key}>
                            <p className="text-xs uppercase tracking-widest text-charcoal/50 mb-2">{group.title}</p>
                            <div className="space-y-2">
                              {items.map((a) => {
                                const checked = form.addon_ids.includes(a.id);
                                return (
                                  <label key={a.id} data-testid={`addon-${a.id}`}
                                    className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-all ${checked ? "border-terracotta bg-terracotta/5" : "border-[#1F2A40] hover:border-charcoal/30"}`}>
                                    <input type="checkbox" checked={checked} onChange={() => toggleAddon(a.id)}
                                      className="mt-1 accent-terracotta" data-testid={`addon-check-${a.id}`} />
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="font-medium text-sm text-charcoal">{a.label}</span>
                                        <span className="text-sm text-terracotta font-semibold">+${a.price.toFixed(0)}</span>
                                      </div>
                                      <p className="text-xs text-charcoal/60 mt-0.5">{a.description}</p>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="bg-[#0B1220] rounded-xl p-4 text-sm border border-[#1F2A40]">
                  <p className="flex justify-between"><span>Labor · {form.estimated_hours}h @ ${h.hourly_rate}/hr</span><span>${laborTotal.toFixed(2)}</span></p>
                  {mountFee > 0 && (
                    <p className="flex justify-between mt-1 text-cognac" data-testid="mount-fee-line">
                      <span>TV installation fee (flat)</span>
                      <span>+${mountFee.toFixed(2)}</span>
                    </p>
                  )}
                  {addonTotal > 0 && (
                    <p className="flex justify-between mt-1 text-charcoal/70">
                      <span>Products ({form.addon_ids.length})</span>
                      <span>${addonTotal.toFixed(2)}</span>
                    </p>
                  )}
                  {bundleEligible && (
                    <p className="flex justify-between mt-1 text-sage" data-testid="bundle-line">
                      <span>TV Bundle · 10% off</span>
                      <span>−${bundleDiscount.toFixed(2)}</span>
                    </p>
                  )}
                  {!bundleEligible && form.category === "tv_mounting" && addonTotal > 0 && (
                    <p className="mt-2 text-xs text-cognac" data-testid="bundle-hint">
                      Tip: add both a TV and a mount bracket to unlock 10% off (labor + products).
                    </p>
                  )}
                  <p className="flex justify-between mt-2 font-semibold text-charcoal border-t border-[#1F2A40] pt-2" data-testid="booking-total">
                    <span>Total{tip > 0 ? " (before tip)" : ""}</span><span>${grandTotal.toFixed(2)}</span>
                  </p>
                  {tip > 0 && (
                    <p className="flex justify-between mt-1 text-sage" data-testid="tip-line">
                      <span>Tip for your Move-in Pro</span>
                      <span>+${tip.toFixed(2)}</span>
                    </p>
                  )}
                </div>

                {/* Tip picker */}
                <div className="bg-[#0B1220] rounded-xl p-4 border border-[#1F2A40]" data-testid="tip-picker">
                  <div className="flex items-center gap-2 text-sm text-charcoal">
                    <Heart className="w-4 h-4 text-terracotta" />
                    <span className="font-medium">Add a tip for your Move-in Pro?</span>
                  </div>
                  <p className="text-[11px] text-charcoal/60 mt-1">100% goes to them — we don&apos;t take a cut on tips.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {TIP_OPTIONS.map((amt) => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, tip_amount: amt }))}
                        data-testid={`tip-opt-${amt}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          Number(form.tip_amount) === amt
                            ? "bg-terracotta text-white border-terracotta"
                            : "bg-transparent text-charcoal/80 border-[#1F2A40] hover:border-terracotta/60"
                        }`}
                      >
                        {amt === 0 ? "No tip" : `$${amt}`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Punctuality & scheduling notice */}
                <div className="bg-[#0B1220] rounded-xl p-4 border border-terracotta/30 text-sm" data-testid="punctuality-notice">
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-terracotta mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-charcoal">Schedule is important — please be on time.</p>
                      <p className="text-charcoal/70 text-[12px] mt-1 leading-relaxed">
                        Move-in demand is heavy this week. Your Move-in Pro is arriving on the clock, and if there&apos;s time available they may continue past the estimate — any extra hours worked will be billed on completion at the same hourly rate.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-[#0B1220] rounded-xl p-4 text-sm border border-[#1F2A40]">
                  {grandTotal > 0 && (
                    <div className="space-y-1.5">
                      <p className="flex justify-between text-charcoal">
                        <span className="font-medium">Due today</span>
                        <span className="font-semibold text-terracotta" data-testid="due-today">${dueToday.toFixed(2)}</span>
                      </p>
                      <p className="text-[11px] text-charcoal/60 leading-snug">
                        25% labor deposit (${laborDeposit.toFixed(2)}){discountedAddons > 0 ? ` + products ($${discountedAddons.toFixed(2)})` : ""}{mountFee > 0 ? ` + $${mountFee.toFixed(2)} mount fee` : ""}{tip > 0 ? ` + $${tip.toFixed(2)} tip` : ""}
                      </p>
                      {laborBalance > 0 && (
                        <>
                          <p className="flex justify-between text-charcoal/80 mt-2">
                            <span>Charged when job is done</span>
                            <span data-testid="balance-due">${laborBalance.toFixed(2)}</span>
                          </p>
                          <p className="text-[11px] text-charcoal/60 leading-snug">
                            We&apos;ll auto-charge the remaining 75% labor on the same card once your Move-in Pro marks the job complete. Any extra hours worked are billed then too.
                          </p>
                        </>
                      )}
                      <p className="text-[11px] text-charcoal/50 leading-snug mt-2 pt-2 border-t border-[#1F2A40]">
                        Cancellation: labor deposit is non-refundable. Products are refundable minus a 25% processing fee up to 24h before service.
                      </p>
                    </div>
                  )}
                </div>
                <Button onClick={submitBooking} data-testid="submit-booking-btn"
                  className="w-full rounded-full h-11 bg-terracotta hover:bg-terracotta/90 text-white btn-lift">
                  {grandTotal > 0 ? `Confirm & pay $${dueToday.toFixed(2)} today` : "Confirm & pay"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
